import React, { useState } from "react";
import {
  type combineType,
  type PlotCardDetaillsInterface,
  type TrailPlotsDataInterface,
  type NewTrialStateInterface,
  type AbLine,
  type BufferZone,
  type Plots,
  type TrialPlot,
  type Replicants,
} from "../../../pages/new-trail/types";
import { notification } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { clearStripLinesCache } from "../../../maps/components/MapComponent/utils";
import { toast } from "react-toastify";

// Geometry preservation utilities for strip-to-plot conversion
interface StripGeometryUpdate {
  stripIndex: number;
  geometry: any;
  area: number;
  length: number;
  width: number;
}

// Geometry utility functions are defined below as exports

const EXTERNAL = "EXTERNAL";
const INTERNAL = "INTERNAL";

type Coordinate2D = [number, number];
type LineEndpoints = [Coordinate2D, Coordinate2D];

const SECURE_RANDOM_MAX_UINT32 = 0x100000000;
const MIN_ANGLE_DIFF = 20; // degrees

const getSecureRandom = (): number => {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    const randomBuffer = new Uint32Array(1);
    cryptoApi.getRandomValues(randomBuffer);
    return randomBuffer[0] / SECURE_RANDOM_MAX_UINT32;
  }

  console.error(
    "Secure random number generation is unavailable; falling back to deterministic value"
  );
  return 0.5;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const parsePointCoordinate = (value: unknown): Coordinate2D | null => {
  if (!Array.isArray(value) || value.length < 2) {
    return null;
  }

  const lng = toFiniteNumber(value[0]);
  const lat = toFiniteNumber(value[1]);
  if (lng == null || lat == null) {
    return null;
  }

  return [lng, lat];
};

const parseLineEndpoints = (
  value: unknown
): [Coordinate2D, Coordinate2D] | null => {
  if (!Array.isArray(value) || value.length < 2) {
    return null;
  }

  const start = parsePointCoordinate(value.at(0));
  const end = parsePointCoordinate(value.at(-1));
  if (!start || !end) {
    return null;
  }

  return [start, end];
};

const getMinBearingDiff = (candidate: number, bearings: number[]): number => {
  if (bearings.length === 0) {
    return 180;
  }

  return bearings.reduce((minDiff, bearing) => {
    const diff = Math.abs(candidate - bearing);
    const circularDiff = Math.min(diff, 180 - diff);
    return Math.min(minDiff, circularDiff);
  }, Number.POSITIVE_INFINITY);
};

const getExistingLineCentres = (existingLines: AbLine[]): Coordinate2D[] => {
  return existingLines
    .map((line) => {
      const endpoints = parseLineEndpoints(
        line?.geojson?.geometry?.coordinates
      );
      if (!endpoints) return null;
      return [
        (endpoints[0][0] + endpoints[1][0]) / 2,
        (endpoints[0][1] + endpoints[1][1]) / 2,
      ] as Coordinate2D;
    })
    .filter((center): center is Coordinate2D => center !== null);
};

const chooseCrossingAngle = (existingBearings: number[]): number => {
  const chosenAngle = getSecureRandom() * 180;
  if (existingBearings.length === 0) {
    return chosenAngle;
  }

  for (let attempt = 0; attempt < 360; attempt++) {
    const candidate = getSecureRandom() * 180;
    if (getMinBearingDiff(candidate, existingBearings) >= MIN_ANGLE_DIFF) {
      return candidate;
    }
  }

  let bestAngle = chosenAngle;
  let bestMinDiff = getMinBearingDiff(chosenAngle, existingBearings);
  for (let candidate = 0; candidate < 180; candidate++) {
    const minDiff = getMinBearingDiff(candidate, existingBearings);
    if (minDiff > bestMinDiff) {
      bestMinDiff = minDiff;
      bestAngle = candidate;
    }
  }

  return bestAngle;
};

const chooseCrossingCenter = (
  firstCenter: Coordinate2D,
  spread: number,
  existingCentres: Coordinate2D[],
  minDistance: number
): Coordinate2D => {
  const [firstCenterLng, firstCenterLat] = firstCenter;

  for (let attempt = 0; attempt < 50; attempt++) {
    const candidateLng = firstCenterLng + (getSecureRandom() * 2 - 1) * spread;
    const candidateLat = firstCenterLat + (getSecureRandom() * 2 - 1) * spread;
    const tooClose = existingCentres.some((center) => {
      return (
        Math.hypot(candidateLng - center[0], candidateLat - center[1]) <
        minDistance
      );
    });

    if (!tooClose) {
      return [candidateLng, candidateLat];
    }
  }

  return firstCenter;
};

const buildLineFromCenterAndAngle = (
  center: Coordinate2D,
  angle: number,
  halfLength: number
): Coordinate2D[] => {
  const [centerLng, centerLat] = center;
  const radians = (angle * Math.PI) / 180;
  const deltaX = Math.cos(radians) * halfLength;
  const deltaY = Math.sin(radians) * halfLength;

  return [
    [centerLng - deltaX, centerLat - deltaY],
    [centerLng + deltaX, centerLat + deltaY],
  ];
};

const createFallbackCrossingLineCoordinates = (
  existingLines: AbLine[]
): Coordinate2D[] => {
  const point = parsePointCoordinate(
    existingLines[0]?.geojson?.geometry?.coordinates
  ) ?? [0, 0];
  const fallbackAngle = (getSecureRandom() * 160 + 10) * (Math.PI / 180);
  const halfLength = 0.0012;
  const deltaX = Math.cos(fallbackAngle) * halfLength;
  const deltaY = Math.sin(fallbackAngle) * halfLength;

  return [
    [point[0] - deltaX + 0.001, point[1] - deltaY + 0.001],
    [point[0] + deltaX + 0.001, point[1] + deltaY + 0.001],
  ];
};

const createCrossingLineForEndpoints = (
  existingLines: AbLine[],
  firstLineEndpoints: LineEndpoints
): Coordinate2D[] => {
  const [start, end] = firstLineEndpoints;
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  const firstCenter: Coordinate2D = [
    (start[0] + end[0]) / 2,
    (start[1] + end[1]) / 2,
  ];
  const halfLength = Math.max(length / 2, 0.001);

  const existingBearings = existingLines
    .map((line) => getLineBearing(line?.geojson?.geometry?.coordinates))
    .filter((bearing): bearing is number => bearing !== null);
  const existingCentres = getExistingLineCentres(existingLines);

  const chosenAngle = chooseCrossingAngle(existingBearings);
  const spread = length * 1.5 || 0.003;
  const selectedCenter = chooseCrossingCenter(
    firstCenter,
    spread,
    existingCentres,
    halfLength * 0.5
  );

  return buildLineFromCenterAndAngle(selectedCenter, chosenAngle, halfLength);
};

/**
 * Returns the bearing in degrees [0, 180) of a LineString geometry.
 * Normalised so that lines in opposite directions compare as equal.
 */
const getLineBearing = (coordinates: unknown): number | null => {
  const endpoints = parseLineEndpoints(coordinates);
  if (!endpoints) return null;
  const [start, end] = endpoints;
  const angle =
    (Math.atan2(end[1] - start[1], end[0] - start[0]) * 180) / Math.PI;
  // Normalise to [0, 180) so that antiparallel lines compare equal
  return ((angle % 180) + 180) % 180;
};

/**
 * Generate coordinates for a new AB line that:
 *  - Is placed at a random position near the field area (based on the first line's scale)
 *  - Has a random bearing that differs from every existing line by at least MIN_ANGLE_DIFF
 *  - Is never at the same location as any existing line
 */
const createCrossingLineCoordinates = (
  existingLines: AbLine[]
): Coordinate2D[] => {
  const firstLineEndpoints = parseLineEndpoints(
    existingLines[0]?.geojson?.geometry?.coordinates
  );
  if (firstLineEndpoints) {
    return createCrossingLineForEndpoints(existingLines, firstLineEndpoints);
  }

  return createFallbackCrossingLineCoordinates(existingLines);
};

// Export geometry utility functions as standalone exports to avoid circular dependency
export const updateTrialPlotsWithStripGeometries = (
  trialPlots: TrialPlot[],
  stripGeometries: StripGeometryUpdate[]
): TrialPlot[] => {
  if (!stripGeometries || stripGeometries.length === 0) {
    return trialPlots;
  }

  return trialPlots.map((trialPlot) => {
    const plot = trialPlot.plot;

    // Handle the new structure: single group with 1×N cells (strips).
    if (plot.isStripPlot && plot.totalStrips && plot.stripIndices) {
      // ONE plot group, N cells — each cell is its OWN geometry: a Polygon when
      // the strip is a single piece, or a MultiPolygon when it's disjoint across
      // a MultiPolygon field's parcels. We store these as a GeometryCollection
      // (geometries[i] = cell/strip i), so the grouping is explicit in the
      // geometry — no flattening, and every consumer knows which polygons belong
      // to the same strip. Rates stay 1 entry per cell (1:1 with geometries).
      const cellGeometries: any[] = [];
      let updatedCount = 0;

      for (let stripIndex = 0; stripIndex < plot.totalStrips; stripIndex++) {
        const matchingGeometry = stripGeometries.find(
          (sg) => sg.stripIndex === stripIndex
        );

        // Extract geometry properly - handle both Feature and Geometry objects
        let actualGeometry = matchingGeometry?.geometry;
        if (actualGeometry && actualGeometry.type === "Feature") {
          actualGeometry = actualGeometry.geometry;
        }

        if (
          (actualGeometry?.type === "Polygon" ||
            actualGeometry?.type === "MultiPolygon") &&
          actualGeometry.coordinates
        ) {
          cellGeometries.push({
            type: actualGeometry.type,
            coordinates: actualGeometry.coordinates,
          });
          updatedCount++;
        } else {
          // Placeholder so the cell still occupies its slot (keeps rates aligned).
          cellGeometries.push({
            type: "Polygon",
            coordinates: [
              [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            ],
          });
        }
      }

      // One cell per strip, each its own Polygon/MultiPolygon.
      return {
        ...trialPlot,
        plot: {
          ...plot,
          geojson: {
            ...plot.geojson,
            geometry: {
              type: "GeometryCollection",
              geometries: cellGeometries,
            } as any,
          },
          geometrySource:
            updatedCount > 0 ? ("strip" as const) : ("manual" as const),
        },
      };
    }

    // Legacy handling for individual strip plots (backward compatibility)
    if (plot.isStripPlot && typeof plot.stripIndex === "number") {
      const matchingGeometry = stripGeometries.find(
        (sg) => sg.stripIndex === plot.stripIndex
      );

      if (matchingGeometry) {
        // Extract geometry properly - handle both Feature and Geometry objects
        let actualGeometry = matchingGeometry.geometry;
        if (actualGeometry && actualGeometry.type === "Feature") {
          actualGeometry = actualGeometry.geometry;
        }

        // Ensure we have valid geometry
        if (!actualGeometry?.type || !actualGeometry?.coordinates) {
          return trialPlot; // Return unchanged if geometry is invalid
        }

        return {
          ...trialPlot,
          plot: {
            ...plot,
            geojson: {
              ...plot.geojson,
              geometry: {
                type: actualGeometry.type,
                coordinates: actualGeometry.coordinates,
              },
            },
            geometrySource: "strip" as const,
            stripArea: matchingGeometry.area,
            stripLength: matchingGeometry.length,
            stripWidth: matchingGeometry.width,
          },
        };
      }
    }

    return trialPlot;
  });
};

// Validate geometry consistency to prevent race conditions
export const validateGeometryConsistency = (
  trialPlots: TrialPlot[],
  expectedStripCount: number
): boolean => {
  const stripPlots = trialPlots.filter((tp) => tp.plot.isStripPlot);

  if (stripPlots.length !== expectedStripCount) {
    return false;
  }

  // Check for missing strip indices
  const stripIndices = stripPlots
    .map((tp) => tp.plot.stripIndex)
    .filter((idx) => typeof idx === "number");
  const expectedIndices = Array.from(
    { length: expectedStripCount },
    (_, i) => i
  );

  const missingIndices = expectedIndices.filter(
    (idx) => !stripIndices.includes(idx)
  );
  if (missingIndices.length > 0) {
    return false;
  }
  return true;
};

// Cleanup function for memory management
export const cleanupStripGeometries = (): void => {
  // Clear any cached geometric data that might accumulate
  clearStripLinesCache();
};

// Export utility functions
export const usePlotCardState = (): any => {
  const [api, contextHolder] = notification.useNotification();
  const { newTrialState, setNewTrialState, trialProtocolData } =
    useNewTrialState();
  const [popoverState, setPopoverState] = useState<
    Record<string, number | null>
  >({
    openPopover: 0,
    openEditPopover: 0,
  });
  const [deleteData, setDeleteData] = useState<combineType>();
  const abHexCodes = [
    "#0092E4",
    "#EE5B3A",
    "#9664F0",
    "#E85D78",
    "#2B9C92",
    "#C17E19",
  ];

  const abClassNames = [
    "ab-blue",
    "ab-orange",
    "ab-purple",
    "ab-pink",
    "ab-teal",
    "ab-yellow",
  ];

  const getAbLineColor = (
    id: number,
    type: "className" | "hexCode"
  ): string => {
    for (let i = 0; i < newTrialState.ab_line.length; i++) {
      if (newTrialState.ab_line[i].id === id) {
        return type === "className" ? abClassNames[i] : abHexCodes[i];
      }
    }
    return "null";
  };

  const handleTickedData = (data: any, indexToUpdate: number): void => {
    if (data.key === "ab_line") {
      const updatedAbLine = newTrialState.ab_line.map(
        (item: any, index: number) => {
          if (item.id === indexToUpdate) {
            return { ...item, selected: !item.selected };
          }
          return item;
        }
      );

      setNewTrialState((prevState: any) => ({
        ...prevState,
        ab_line: updatedAbLine,
      }));
    }
  };

  const toggleVisibility = (
    index: number,
    data: { key: keyof NewTrialStateInterface; id?: number },
    keyName?: string | undefined
  ): void => {
    let currentVal: any;
    if (data.key === "trial_plots" && keyName === undefined) {
      currentVal = newTrialState[data.key]?.map((val: TrialPlot, i: number) => {
        if (i === index) {
          return {
            ...val,
            plot: { ...val.plot, visible: !val?.plot.visible },
          };
        }
        return val;
      });
    } else if (data.key === "trial_plots" && keyName === "replicant") {
      currentVal = newTrialState[data.key]?.map((val: TrialPlot) => {
        return {
          ...val,
          replicants: val.replicants?.map((replicant: Replicants) => {
            if (replicant.id === data.id) {
              return {
                ...replicant,
                visible: !replicant.visible,
              };
            }
            return replicant;
          }),
        };
      });
    } else {
      const value = newTrialState[data.key];
      if (Array.isArray(value)) {
        currentVal = value.map((val: any, i: number) => {
          if (i === index) {
            return { ...val, visible: !val?.visible };
          }
          return val;
        });
      } else {
        // Handle non-array properties (like stripsData)
        currentVal = value;
      }
    }
    setNewTrialState((prevState: NewTrialStateInterface) => ({
      ...prevState,
      [data.key as string]: currentVal,
    }));
  };

  const handleAddData = (addData: PlotCardDetaillsInterface): number => {
    const currentAbLines = newTrialState.ab_line ?? [];
    const nextIndex = currentAbLines.length + 1;
    // Only activate strip mode for strip-type protocols
    const isStripProtocol =
      trialProtocolData?.trial_application_method === "strip";

    // Copy geojson from the last existing AB line so new lines inherit actual coordinates
    const lastAbLine =
      currentAbLines.length > 0
        ? currentAbLines[currentAbLines.length - 1]
        : null;
    const defaultGeojson = lastAbLine?.geojson
      ? JSON.parse(JSON.stringify(lastAbLine.geojson))
      : {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [0, 0],
          },
        };

    // Keep first AB line as [0, 0] placeholder so map logic can initialize it
    // inside the field polygon. For subsequent AB lines, force crossed geometry.
    if (currentAbLines.length > 0) {
      defaultGeojson.geometry = {
        type: "LineString",
        coordinates: createCrossingLineCoordinates(currentAbLines),
      };
    }

    const cardData: AbLine = {
      id: Date.now(),
      selected: true,
      // first AB line is selected for strips ONLY when this is a strip-type protocol
      selectedForStrips: isStripProtocol && currentAbLines.length === 0,
      name: `${addData?.name?.split("+")[0].trim()} ${nextIndex}`,
      // Show the AB line on the map immediately so middle/grid lines are visible during configuration.
      visible: true,
      gridWidth: 20,
      grid_width: 20,
      // Keep new AB line hidden from the card until popup Apply finalizes it.
      key: "__AB_DRAFT__",
      geojson: defaultGeojson,
    };

    let nextStripsData = newTrialState.stripsData;

    // Create default stripsData immediately when first AB line is added — STRIP PROTOCOL ONLY
    if (!nextStripsData && currentAbLines.length === 0 && isStripProtocol) {
      nextStripsData = {
        isStripMode: true,
        appliedFromPopup: false,
        count: 2,
        selectedAbLineIndex: 0,
        offsets: [0],
        spacing: cardData.grid_width ?? 20,
        createdAt: new Date().toISOString(),
        version: "1.0",
        selectedAbLine: {
          name: cardData.name,
          gridWidth: cardData.grid_width ?? 20,
          geojson: cardData.geojson,
        },
      };
    }

    if (currentAbLines.length > 0 && nextStripsData?.isStripMode) {
      setNewTrialState((prevState: NewTrialStateInterface) => ({
        ...prevState,
        ab_line: [...currentAbLines, cardData],
      }));
    } else {
      setNewTrialState((prevState: NewTrialStateInterface) => ({
        ...prevState,
        ab_line: [...currentAbLines, cardData],
        stripsData: nextStripsData,
      }));
    }

    return cardData.id as number;
  };

  const handleBufferZoneData = (
    data: number,
    openPopoverKey?: string | undefined,
    val?: number | undefined
  ): void => {
    if (openPopoverKey === "openEditPopover") {
      setNewTrialState((prevState: NewTrialStateInterface) => {
        const updatedBufferZone = (prevState.buffer_zone ?? []).map(
          (zone: BufferZone) => {
            if (zone.id === val) {
              return {
                ...zone,
                type: data < 0 ? EXTERNAL : INTERNAL,
                size: data,
              };
            }
            return zone;
          }
        );

        return {
          ...prevState,
          buffer_zone: updatedBufferZone,
        };
      });
    } else {
      const zoneData: BufferZone = {
        id: new Date().getTime(),
        type: data < 0 ? EXTERNAL : INTERNAL,
        name: "Buffer Zone",
        key: "Buffer Zone",
        size: data,
        visible: true,
      };
      setNewTrialState((prevState: NewTrialStateInterface) => ({
        ...prevState,
        buffer_zone: [zoneData],
      }));
    }
  };

  const handleMachineGridData = (width: number, index?: number): void => {
    // Validate width input - must be a valid positive number
    // Skip validation if null/undefined (field being cleared)
    if (width === null || width === undefined) {
      return; // Don't update state with null values
    }

    if (Number.isNaN(width) || width <= 0) {
      console.warn(`Invalid grid width value: ${width}`);
      toast.error("Invalid grid width value. Please enter a positive number.");
      return; // Don't update state with invalid values
    }

    setNewTrialState((prevState: NewTrialStateInterface) => {
      const abLines = prevState.ab_line;
      abLines[index ?? 0].grid_width = width;

      // Check if this AB line is selected for strips and if we have existing strips data
      const updatedAbLine = abLines[index ?? 0];
      let updatedStripsData = prevState.stripsData;

      if (
        updatedAbLine.selectedForStrips &&
        prevState.stripsData &&
        prevState.stripsData.count
      ) {
        // Completely reset strip offsets based on new width - start fresh
        const count = prevState.stripsData.count;
        let newOffsets: number[] = [];

        if (count === 2) {
          newOffsets = [0]; // 2 strips = AB line only
        } else if (count === 3) {
          newOffsets = [0, width]; // 3 strips = AB line + width offset
        } else if (count > 3) {
          newOffsets = [0]; // Start with AB line at 0
          for (let i = 1; i < count - 1; i++) {
            newOffsets.push(i * width);
          }
        }

        // Completely replace the strips data with fresh configuration
        updatedStripsData = {
          isStripMode: true,
          count,
          selectedAbLineIndex: index ?? 0,
          offsets: newOffsets,
          spacing: width,
          // Reset areas and details - they will be recalculated
          areas: undefined,
          stripDetails: undefined,
          createdAt: new Date().toISOString(),
          version: "1.0",
        };
      }

      const newState = {
        ...prevState,
        ab_line: abLines,
        stripsData: updatedStripsData,
      };
      return newState;
    });

    // Clear strip lines cache after state update to force complete recalculation
    setTimeout(() => {
      clearStripLinesCache();
    }, 100); // Small delay to ensure state is updated first
  };

  const handleAlignmentSelectionChange = (index: number): void => {
    setNewTrialState((prevState: NewTrialStateInterface) => ({
      ...prevState,
      ab_line: prevState.ab_line.map((ab: AbLine, i: number) => ({
        ...ab,
        selectedForAlignment: i === index,
      })),
    }));
  };

  const handleAbLineData = (name: string, index?: number): void => {
    setNewTrialState((prevState: NewTrialStateInterface) => {
      const abLines = prevState.ab_line;
      abLines[index ?? 0].name = name ?? 0;
      return {
        ...prevState,
        ab_line: abLines,
      };
    });
  };

  const handleTrailPlotsData = (
    rows: number,
    columns: number,
    plotWidth: number,
    plotHeight: number,
    openPopoverKey?: string | undefined,
    val?: number | undefined,
    indexKey?: number | undefined,
    isFromStrips?: boolean
  ): void => {
    if (openPopoverKey === "openEditPopover") {
      setNewTrialState((prevState: NewTrialStateInterface) => {
        const updatedTrialPlots = prevState.trial_plots.map(
          (item: TrialPlot, i: number) => {
            if (i === indexKey) {
              const updatedReplicants = item.replicants?.map(
                (replicant: Replicants) => ({
                  ...replicant,
                  rows,
                  columns,
                  plotWidth,
                  plotHeight,
                  column_size: plotWidth,
                  row_size: plotHeight,
                  geojson: {
                    type: "Feature",
                    geometry: {
                      type: "Point",
                      coordinates: [0, 0],
                    },
                  },
                })
              );
              return {
                ...item,
                plot: {
                  ...item.plot,
                  rows,
                  columns,
                  plotWidth,
                  plotHeight,
                  column_size: plotWidth,
                  row_size: plotHeight,
                  geojson: {
                    type: "Feature",
                    geometry: {
                      type: "Point",
                      coordinates: [0, 0],
                    },
                  },
                },
                replicants: updatedReplicants,
              };
            }
            return item;
          }
        );
        return {
          ...prevState,
          trial_plots: updatedTrialPlots,
        };
      });
    } else {
      // Count existing strips plots for proper naming
      const existingStripsPlots =
        newTrialState?.trial_plots?.filter((plot: TrialPlot) =>
          plot.plot.name.startsWith("Strips-plot-")
        ) || [];

      const newTitle = isFromStrips
        ? `Strips-plot-${existingStripsPlots.length + 1}`
        : `Plots ${+newTrialState?.trial_plots?.length + 1}`;

      const trialData: Plots = {
        id: new Date().getTime(),
        key: "Trial Plots",
        name: newTitle,
        visible: true,
        rows,
        columns,
        column_size: plotWidth,
        row_size: plotHeight,
        plotWidth,
        plotHeight,
        // Strip-specific properties for geometry preservation
        ...(isFromStrips && {
          isStripPlot: true,
          stripIndex: existingStripsPlots.length, // Assign next available strip index
          stripArea: 0, // Will be updated when map calculates actual geometry
          stripLength: 0,
          stripWidth: 0,
          geometrySource: "manual", // Initially manual, will be updated to 'strip' when geometry is calculated
        }),
        geojson: {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [0, 0],
          },
        },
      };
      setNewTrialState((prevState: any) => {
        return {
          ...prevState,
          trial_plots: [
            ...(prevState?.trial_plots ?? null),
            { plot: trialData },
          ],
        };
      });
    }
  };

  const handleReplicantData = (
    dataValue: TrailPlotsDataInterface,
    i: number
  ): void => {
    const replicantsTitle = `${
      newTrialState?.trial_plots[i]?.replicants?.length === undefined ||
      Number.isNaN(newTrialState?.trial_plots[i]?.replicants?.length)
        ? "Replicant 1"
        : `Replicant ${+newTrialState?.trial_plots[i]?.replicants?.length + 1}`
    }`;
    const newTitle = replicantsTitle;
    const { plot } = dataValue;
    const addReplicantData: Replicants = {
      ...plot,
      key: "trial_plots",
      id: new Date().getTime(),
      parentId: dataValue.plot.id,
      visible: true,
      name: newTitle,
      geojson: {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [0, 0],
        },
      },
    };

    setNewTrialState((prevState: NewTrialStateInterface) => ({
      ...prevState,
      trial_plots: (prevState?.trial_plots ?? []).map(
        (plot: TrialPlot, index: number) =>
          index === i
            ? {
                ...plot,
                replicants: [...(plot?.replicants ?? []), addReplicantData],
              }
            : plot
      ),
    }));
  };

  const handleDeleteData = (
    deletedData: combineType,
    key: keyof NewTrialStateInterface,
    replicantKey?: string | undefined
  ): any => {
    const isReplicant =
      key === "trial_plots" && replicantKey === "replicantKey";
    const isDeletingAbLine = key === "ab_line";
    const isDeletingLastAbLine =
      isDeletingAbLine && newTrialState.ab_line?.length === 1;

    setNewTrialState((prevState: any) => {
      if (key === "ab_line") {
        let remainingAbLines = (prevState.ab_line ?? []).filter(
          (ab: AbLine) => ab.id !== deletedData.id
        );

        let nextStripsData = prevState.stripsData;
        let nextTrialPlots = prevState.trial_plots ?? [];

        if (remainingAbLines.length === 0) {
          // hard clear
          nextStripsData = undefined;
          nextTrialPlots = nextTrialPlots.filter(
            (tp: TrialPlot) =>
              !tp?.plot?.isStripPlot &&
              !(tp?.plot?.name ?? "").startsWith("Strips-plot-")
          );
        } else {
          let selectedIndex = remainingAbLines.findIndex(
            (ab: any) => ab.selectedForStrips
          );
          if (selectedIndex < 0) selectedIndex = 0;

          remainingAbLines = remainingAbLines.map((ab: any, idx: any) => ({
            ...ab,
            selectedForStrips: idx === selectedIndex,
          }));

          if (nextStripsData?.isStripMode) {
            const selectedAb = remainingAbLines[selectedIndex];
            const width = selectedAb?.grid_width ?? selectedAb?.gridWidth ?? 20;

            nextStripsData = {
              ...nextStripsData,
              selectedAbLineIndex: selectedIndex,
              spacing: width,
              selectedAbLine: {
                name: selectedAb.name,
                gridWidth: width,
                geojson: selectedAb.geojson,
              },
            };
          }
        }

        return {
          ...prevState,
          ab_line: remainingAbLines,
          stripsData: isDeletingLastAbLine ? null : nextStripsData,
          trial_plots: nextTrialPlots,
        };
      }
      const updatedTrialPlots = (prevState.trial_plots ?? []).map(
        (val: TrialPlot) => {
          const filteredReplicants = val.replicants?.filter(
            (replicantData: Replicants) => replicantData.id !== deletedData.id
          );
          return {
            ...val,
            replicants: filteredReplicants,
          };
        }
      );

      if (isReplicant) {
        return {
          ...prevState,
          trial_plots: updatedTrialPlots,
        };
      }

      if (key === "trial_plots") {
        return {
          ...prevState,
          trial_plots: (prevState.trial_plots ?? []).filter((val: any) =>
            replicantKey === ""
              ? val.plot?.id !== deletedData.id
              : val.id !== deletedData.id
          ),
        };
      }

      if (key === "buffer_zone") {
        return {
          ...prevState,
          buffer_zone: (prevState.buffer_zone ?? []).filter(
            (val: BufferZone) => val.id !== deletedData.id
          ),
        };
      }

      if (key === "machinery_grid") {
        return {
          ...prevState,
          machinery_grid: (prevState.machinery_grid ?? []).filter(
            (val: any) => val.id !== deletedData.id
          ),
        };
      }

      return prevState;
    });

    if (isDeletingAbLine) {
      clearStripLinesCache();
      globalThis.dispatchEvent(new CustomEvent("force-clear-strips"));
    }

    if (isDeletingLastAbLine) {
      // clear provider-level strips state too

      setTimeout(() => {
        clearStripLinesCache();
        globalThis.dispatchEvent(new CustomEvent("force-clear-strips"));
      }, 100);
    }

    const notificationMessage = React.createElement(
      "div",
      { className: "title-sec" },
      React.createElement(CheckCircleOutlined),
      `${deletedData.name} Deleted`
    );

    api.open({
      message: notificationMessage,
      className: "delete-plot-notification",
      duration: 2,
    });
  };

  return {
    handleReplicantData,
    handleAddData,
    handleBufferZoneData,
    handleMachineGridData,
    handleAbLineData,
    handleAlignmentSelectionChange,
    handleTrailPlotsData,
    toggleVisibility,
    deleteData,
    setDeleteData,
    popoverState,
    setPopoverState,
    api,
    contextHolder,
    handleDeleteData,
    handleTickedData,
    getAbLineColor,
  };
};
